import { Link as RouterLink, useNavigate } from "react-router-dom";
import { usePortal, type ClientAdminSummary } from "@app/portal-sdk";
import { Alert, AppBadge, Button, DataTable, EmptyState, PageHeader, Stack, Typography } from "@app/ui-kit";
import { HealthWord } from "../components/Stoplight";
import Link from "@mui/material/Link";
import { LoadingScreen } from "../components/LoadingScreen";
import { formatDate } from "../lib/format";
import { useApiQuery } from "../lib/useApiQuery";

export const ClientsList = () => {
  const navigate = useNavigate();
  const { api } = usePortal();
  const clients = useApiQuery<ClientAdminSummary[]>(
    () => api.request<ClientAdminSummary[]>("/admin/clients"),
    [api]
  );

  const newClientButton = (
    <Button variant="contained" onClick={() => navigate("/admin/clients/new")}>
      New client
    </Button>
  );

  return (
    <Stack spacing={5}>
      <PageHeader
        eyebrow="Admin Console"
        title="Clients"
        subtitle="Every client portal you run. Open one to manage members, applications and branding."
        actions={newClientButton}
      />

      {clients.error && <Alert severity="error">{clients.error}</Alert>}
      {clients.loading && !clients.data ? (
        <LoadingScreen />
      ) : (clients.data ?? []).length === 0 ? (
        <EmptyState
          eyebrow="No Clients"
          title="No clients yet"
          description="Create your first client, then invite their team and enable the applications they need."
          action={newClientButton}
        />
      ) : (
        <DataTable
          columns={[
            { key: "name", label: "Name" },
            { key: "slug", label: "Portal address" },
            { key: "members", label: "Members" },
            { key: "apps", label: "Apps" },
            { key: "status", label: "Status" },
            { key: "health", label: "Health" },
            { key: "fiscal", label: "Fiscal year" },
            { key: "created", label: "Created" }
          ]}
          rows={(clients.data ?? []).map((client) => ({
            name: (
              <Link component={RouterLink} to={`/admin/clients/${client.id}`} underline="hover" sx={{ fontWeight: 600 }}>
                {client.name}
              </Link>
            ),
            slug: <code>/c/{client.slug}</code>,
            members: client.memberCount,
            apps: client.appCount,
            status: (
              <AppBadge
                label={client.status === "active" ? "Active" : "Suspended"}
                color={client.status === "active" ? "success" : "warning"}
              />
            ),
            // SHL-3: the colour with its word, so thirty rows read down the column; a dash where BKDP has not set one.
            health: client.health ? <HealthWord health={client.health} /> : <Typography component="span" variant="body2" sx={{ color: "text.disabled" }}>not set</Typography>,
            fiscal: <AppBadge label={client.fiscalCalendarConfirmed ? "Confirmed" : "Unconfirmed"} color={client.fiscalCalendarConfirmed ? "success" : "warning"} variant={client.fiscalCalendarConfirmed ? "filled" : "outlined"} />,
            created: formatDate(client.createdAt)
          }))}
        />
      )}
    </Stack>
  );
};
